import React, { useMemo, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { appProfiler } from '../../utils/appProfiler';

const FastSegments_inst = { count: 0, lastLog: performance.now() };
import { useFrame } from '../../utils/useFrameP1'; // P1: igual ao do r3f; com ?dbg=1 mede o tempo dos handlers
import { positionFor } from '../../utils/anchoredNav';

const FastSegments = React.memo(React.forwardRef(({ points, vertexColors, color = '#ffffff', opacity = 1, transparent = false, depthWrite = true, depthTest = true, dashed = false, dashSize = 3, gapSize = 1, frustumCulled = true, lineWidth = 1, renderOrder = 0, dynamicData, clippingPlanes }: any, externalRef: any) => {
  const lineRef = useRef<THREE.LineSegments>(null);
  const lastIntOffsetRef = useRef<number | null>(null);
  const lastDataRef = useRef<any>(null);

  const geometry = useMemo(() => {
    const geom = new THREE.BufferGeometry();
    return geom;
  }, []);

  const material = useMemo(() => {
    const matColor = vertexColors ? 0xffffff : new THREE.Color(color);
    const activePlanes = (clippingPlanes && clippingPlanes.length > 0) ? clippingPlanes : null;
    if (dashed) {
      return new THREE.LineDashedMaterial({
        color: matColor,
        vertexColors: !!(vertexColors && vertexColors.length > 0),
        linewidth: lineWidth,
        opacity,
        transparent: transparent || opacity < 1,
        depthWrite,
        depthTest,
        dashSize,
        gapSize,
        toneMapped: false,
        clippingPlanes: activePlanes,
      });
    } else {
      return new THREE.LineBasicMaterial({
        color: matColor,
        vertexColors: !!(vertexColors && vertexColors.length > 0),
        linewidth: lineWidth,
        opacity,
        transparent: transparent || opacity < 1,
        depthWrite,
        depthTest,
        toneMapped: false,
        clippingPlanes: activePlanes,
      });
    }
  }, [color, !!vertexColors, lineWidth, opacity, transparent, depthWrite, depthTest, dashed, dashSize, gapSize, clippingPlanes]);

  useEffect(() => {
    if (material) {
      material.clippingPlanes = (clippingPlanes && clippingPlanes.length > 0) ? clippingPlanes : null;
      material.needsUpdate = true;
    }
  }, [material, clippingPlanes]);


  useFrame(() => {
    if (!dynamicData || !geometry) return;
    const { data, historyOffsetRef, windowSize = 1000, extractor, colorFn, step = 1 } = dynamicData;
    if (!data || data.length === 0) return;
    
    const total = data.length;
    // posição relativa a ESTES dados (F2): `newestAbs` = índice ancorado da vela mais nova da série do lote
    const intOffset = Math.floor(positionFor(historyOffsetRef, dynamicData.newestAbs ?? NaN) || 0);

    // DIRTY CHECKING: Recalculate if integer shift occurs OR if data reference changes
    if (lastIntOffsetRef.current === intOffset && lastDataRef.current === data) return;
    lastIntOffsetRef.current = intOffset;
    lastDataRef.current = data;
    FastSegments_inst.count++;
    const now = performance.now();
    if (now - FastSegments_inst.lastLog > 1000) {
      console.log('[PERF] FastSegments heavy loop runs/sec:', FastSegments_inst.count);
      FastSegments_inst.count = 0;
      FastSegments_inst.lastLog = now;
    }


    const end = Math.max(windowSize, total - intOffset);
    const start = Math.max(0, end - windowSize);
    const sliceLen = end - start;
    
    const numSegments = Math.floor(sliceLen / step);
    const requiredLength = numSegments * 6; // 2 points * 3 coords
    
    let posAttr = geometry.getAttribute('position') as THREE.BufferAttribute;
    if (!posAttr || posAttr.array.length < requiredLength) {
      const newCap = Math.max(requiredLength * 2, 3000);
      posAttr = new THREE.BufferAttribute(new Float32Array(newCap), 3);
      posAttr.setUsage(THREE.DynamicDrawUsage);
      geometry.setAttribute('position', posAttr);
    }
    
    let colAttr = geometry.getAttribute('color') as THREE.BufferAttribute;
    if (colorFn) {
      if (!colAttr || colAttr.array.length < requiredLength) {
        const newCap = Math.max(requiredLength * 2, 3000);
        colAttr = new THREE.BufferAttribute(new Float32Array(newCap), 3);
        colAttr.setUsage(THREE.DynamicDrawUsage);
        geometry.setAttribute('color', colAttr);
      }
    }
    
    const pos = posAttr.array as Float32Array;
    const col = colorFn ? (colAttr.array as Float32Array) : null;
    
    let idx = 0;
    for (let i = start; i < end; i += step) {
      const d = data[i];
      if (!d) continue;
      
      const res = extractor(d, i - start, sliceLen - 1);
      if (!res) continue;
      
      const [x1, y1, z1, x2, y2, z2] = res;
      pos[idx * 6] = x1; pos[idx * 6 + 1] = y1; pos[idx * 6 + 2] = z1;
      pos[idx * 6 + 3] = x2; pos[idx * 6 + 4] = y2; pos[idx * 6 + 5] = z2;
      
      if (col && colorFn) {
         const cVal = colorFn(d, i - start, sliceLen - 1);
         const cObj = new THREE.Color(cVal);
         col[idx*6] = cObj.r; col[idx*6+1] = cObj.g; col[idx*6+2] = cObj.b;
         col[idx*6+3] = cObj.r; col[idx*6+4] = cObj.g; col[idx*6+5] = cObj.b;
      }
      idx++;
    }
    
    posAttr.needsUpdate = true;
    if (colAttr) colAttr.needsUpdate = true;
    geometry.setDrawRange(0, idx * 2);
    
    if (dashed && lineRef.current) {
       lineRef.current.computeLineDistances();
    }
  });

  useEffect(() => {
    if (dynamicData) return;
    if (!geometry) return;
    
    let validPoints: Float32Array | number[];
    if (points instanceof Float32Array) {
      validPoints = points;
    } else {
      let flatPoints: number[] = [];
      if (points) {
        for (const p of points) {
          if (Array.isArray(p)) { 
             flatPoints.push(p[0] ?? 0, p[1] ?? 0, p[2] ?? 0); 
           } else if (p && typeof p.x === 'number') { 
             flatPoints.push(p.x, p.y, p.z); 
           } else { 
             flatPoints.push(typeof p === 'number' && isFinite(p) ? p : 0); 
           }
        }
      }
      validPoints = flatPoints.map((p: number) => (!isFinite(p) || isNaN(p)) ? 0 : p);
    }
    
    let validColors: Float32Array | number[] = [];
    if (vertexColors) {
      if (vertexColors instanceof Float32Array) {
        validColors = vertexColors;
      } else {
        let flatColors: number[] = [];
        for (const c of vertexColors) {
          if (Array.isArray(c)) { 
             flatColors.push(c[0] ?? 0, c[1] ?? 0, c[2] ?? 0); 
           } else if (c && typeof c.r === 'number') { 
             flatColors.push(c.r, c.g, c.b); 
           } else { 
             flatColors.push(typeof c === 'number' && isFinite(c) ? c : 0); 
           }
        }
        validColors = flatColors.map((c: number) => (!isFinite(c) || isNaN(c)) ? 0 : Math.max(0, Math.min(1, c)));
      }
    }
    
    const posAttr = geometry.getAttribute('position') as THREE.BufferAttribute;
    if (!posAttr) {
      const initialCapacity = Math.max(validPoints.length * 2, 3000);
      const array = new Float32Array(initialCapacity);
      const newPosAttr = new THREE.BufferAttribute(array, 3);
      newPosAttr.setUsage(THREE.DynamicDrawUsage);
      geometry.setAttribute('position', newPosAttr);
    } else if (validPoints.length > posAttr.array.length) {
      let newCapacity = posAttr.array.length * 2;
      while (newCapacity < validPoints.length) {
        newCapacity *= 2;
      }
      const newArray = new Float32Array(newCapacity);
      const newPosAttr = new THREE.BufferAttribute(newArray, 3);
      newPosAttr.setUsage(THREE.DynamicDrawUsage);
      geometry.setAttribute('position', newPosAttr);
    }

    appProfiler.time('WebGL:FastLine:BufferAttributeUpload', () => {
      const currentPosAttr = geometry.getAttribute('position') as THREE.BufferAttribute;
      (currentPosAttr.array as Float32Array).set(validPoints);
      currentPosAttr.needsUpdate = true;
      geometry.setDrawRange(0, validPoints.length / 3);
      
      if (vertexColors && validColors.length > 0) {
        const colAttr = geometry.getAttribute('color') as THREE.BufferAttribute;
        
        if (!colAttr) {
          const initialCapacity = Math.max(validColors.length * 2, 3000);
          const array = new Float32Array(initialCapacity);
          const newColAttr = new THREE.BufferAttribute(array, 3);
          newColAttr.setUsage(THREE.DynamicDrawUsage);
          geometry.setAttribute('color', newColAttr);
        } else if (validColors.length > colAttr.array.length) {
          let newCapacity = colAttr.array.length * 2;
          while (newCapacity < validColors.length) {
            newCapacity *= 2;
          }
          const newArray = new Float32Array(newCapacity);
          const newColAttr = new THREE.BufferAttribute(newArray, 3);
          newColAttr.setUsage(THREE.DynamicDrawUsage);
          geometry.setAttribute('color', newColAttr);
        }

        const currentColAttr = geometry.getAttribute('color') as THREE.BufferAttribute;
        (currentColAttr.array as Float32Array).set(validColors);
        currentColAttr.needsUpdate = true;
      }
      
      if (dashed && lineRef.current) {
        lineRef.current.computeLineDistances();
      }
    });
  }, [geometry, points, vertexColors, dashed]);

  useEffect(() => {
    return () => {
      if (geometry) geometry.dispose();
    };
  }, [geometry]);

  useEffect(() => {
    return () => {
      if (material) material.dispose();
    };
  }, [material]);

  const lineObject = useMemo(() => {
    const l = new THREE.LineSegments(geometry, material);
    return l;
  }, [geometry, material]);

  useEffect(() => {
    if (lineObject) {
      lineObject.renderOrder = renderOrder;
      lineRef.current = lineObject;
      if (externalRef) externalRef.current = lineObject;
    }
  }, [lineObject, renderOrder]);

  if (!dynamicData && (!points || points.length === 0)) return null;

  return (
    <primitive object={lineObject} frustumCulled={frustumCulled} />
  );
}));

export default FastSegments;
